import { OverlayContainer, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  addDays,
  isBefore,
  isEqual,
  lastDayOfMonth,
  lastDayOfWeek,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { combineLatest, startWith, Subject } from 'rxjs';

import { resizeRxUtils } from '@blaze-shared/shared-util';
import { toFloat, type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';

import { CalendarDay } from '../../../components/calendar-dates-view';
import {
  RelativeFlexibleConnectedPositionStrategyBuilder,
  TargetedOverlay,
  TargetedOverlayContainer,
} from '../../../utils/elements/overlay.utils';
import { Calendar, CalendarEvent, CalendarTimeFormat } from '../calendar.models';
import { getNewCalendarEvent } from '../utils/calendar-event.utils';

type CellModel = {
  date: Date;
  monthLabel: boolean;
  events: CalendarEvent[];
};

@UntilDestroy()
@Component({
  selector: 'blaze-calendar-month-view',
  templateUrl: './calendar-month-view.component.html',
  styleUrls: ['./calendar-month-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: TargetedOverlay },
    { provide: OverlayContainer, useClass: TargetedOverlayContainer },
    { provide: RelativeFlexibleConnectedPositionStrategyBuilder },
  ],
  standalone: false,
})
export class CalendarMonthViewComponent implements AfterViewInit, OnChanges, OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly overlay = inject(TargetedOverlay);
  private readonly relativeFlexibleConnectedPositionStrategyBuilder = inject(
    RelativeFlexibleConnectedPositionStrategyBuilder,
  );
  private readonly targetedOverlayContainer = inject(OverlayContainer);

  add = output<Partial<CalendarEvent>>();
  dateSelected = output<Date>();
  delete = output<CalendarEvent>();
  details = output<CalendarEvent>();
  edit = output<CalendarEvent>();

  @Input() calendars: Calendar[] = [];
  @Input() componentStyles: Record<string, string> = {};
  @Input() defaultDurationMinutes = 60;
  @Input() eventFontSize: ValueWithUnitOfMeasure = '13px';
  @Input() firstDayOfWeek: CalendarDay = CalendarDay.Monday;
  @Input() hideWeekends = false;
  @Input() isAddEnabled = false;
  @Input() isDeleteEnabled = false;
  @Input() isDetailsViewEnabled = false;
  @Input() isEditEnabled = false;
  @Input() selectedDate: Date = new Date();
  @Input() timeFormat: CalendarTimeFormat = CalendarTimeFormat['12H'];

  @ViewChild('popupTemplate') popupTemplate?: TemplatePortal<unknown>;
  @ViewChild('overlayTarget') overlayTarget?: ElementRef<HTMLElement>;

  private overlayRef?: OverlayRef;

  model: CellModel[][] = [];
  eventStyles: Map<CalendarEvent | undefined, Record<string, string>> = new Map();
  selectedCell?: CellModel;
  eventsPerCell = 3;
  eventCommonStyles: Record<string, string> = {};
  recalculateSize$$: Subject<void> = new Subject<void>();

  trackCell = (_index: number, cell: CellModel): number => cell.date.getTime();
  trackRow = (_index: number, [cell]: CellModel[]): number => cell.date.getTime();

  get timePipeFormat(): string {
    return this.timeFormat === CalendarTimeFormat['12H'] ? 'h:mma' : 'H:mm';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.eventFontSize) {
      this.eventCommonStyles = { fontSize: this.eventFontSize };
    }
    this.updateModel();
    if (changes.selectedDate) {
      this.closeDay();
    }
    if (changes.eventFontSize || changes.selectedDate) {
      setTimeout(() => {
        this.recalculateSize$$.next(undefined);
      });
    }
  }

  ngAfterViewInit(): void {
    (this.targetedOverlayContainer as TargetedOverlayContainer).targetElement =
      this.overlayTarget?.nativeElement;

    combineLatest({
      observer: resizeRxUtils.fromResizeObserver(this.elementRef?.nativeElement),
      recalculateSize: this.recalculateSize$$.pipe(startWith(undefined)),
    })
      .pipe(untilDestroyed(this))
      .subscribe(({ observer: [entry] }) => {
        const cellHeight = entry.target.querySelector('.col')?.clientHeight;
        if (!cellHeight) {
          return;
        }
        const placeholder = entry.target.querySelector('.col.placeholder');
        if (!placeholder) {
          return;
        }
        const event = placeholder.querySelector<HTMLElement>('.event');
        if (!event) {
          return;
        }
        const eventStyle = getComputedStyle(event);
        const eventHeightPx =
          event.offsetHeight +
          Math.floor(toFloat(eventStyle.marginTop)) +
          Math.floor(toFloat(eventStyle.marginBottom));
        if (!eventHeightPx) {
          return;
        }
        const cellStyle = getComputedStyle(placeholder);
        const cellPaddingsPx =
          placeholder.clientHeight -
          eventHeightPx -
          Math.floor(toFloat(cellStyle.paddingTop)) -
          Math.floor(toFloat(cellStyle.paddingBottom));
        this.eventsPerCell = Math.floor((cellHeight - cellPaddingsPx) / eventHeightPx) - 1;
        this.cdr.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this.closeDay();
  }

  dateClicked(date: Date): void {
    this.dateSelected.emit(date);
  }

  showDay(cell: CellModel, event: Event): void {
    if (!(event.target instanceof HTMLElement) || !this.popupTemplate) {
      return;
    }
    this.closeDay();

    this.selectedCell = cell;
    this.overlayRef = this.overlay.create({
      positionStrategy: this.relativeFlexibleConnectedPositionStrategyBuilder
        .build(event.target)
        .withContainerRef(this.elementRef)
        .withPositions([
          { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
        ]),
    });
    this.overlayRef.attach(this.popupTemplate);
  }

  closeDay(): void {
    this.selectedCell = undefined;
    this.overlayRef?.detach();
    this.overlayRef?.dispose();
  }

  showDetails(event: CalendarEvent): void {
    if (!this.isDetailsViewEnabled) {
      return;
    }
    this.details.emit(event);
  }

  addEvent(date: Date): void {
    if (!this.isAddEnabled) {
      return;
    }
    this.add.emit(getNewCalendarEvent(date, this.defaultDurationMinutes));
  }

  editEvent(event: CalendarEvent): void {
    if (!this.isEditEnabled) {
      return;
    }
    this.edit.emit(event);
  }

  removeEvent(event: CalendarEvent): void {
    if (!this.isDeleteEnabled) {
      return;
    }
    this.delete.emit(event);
  }

  private updateModel(): void {
    const firstDate = startOfWeek(startOfMonth(this.selectedDate), {
      weekStartsOn: this.firstDayOfWeek,
    });
    const lastDate = addDays(
      lastDayOfWeek(lastDayOfMonth(this.selectedDate), { weekStartsOn: this.firstDayOfWeek }),
      1,
    );

    this.model = [];
    let row = 0,
      col = 0;
    const monthsLabels: Record<number, boolean> = {};
    for (let date = firstDate; isBefore(date, lastDate); date = addDays(date, 1)) {
      if (!this.hideWeekends || (date.getDay() !== 0 && date.getDay() !== 6)) {
        this.model[row] = [
          ...(this.model[row] ?? []),
          {
            date,
            monthLabel: !monthsLabels[date.getMonth()],
            events: this.calendars
              .flatMap(
                ({ events }) =>
                  events[date.getFullYear()]?.[date.getMonth()]?.[date.getDate()] ?? [],
              )
              .sort((e1, e2) => (isBefore(e1.startDate, e2.startDate) ? -1 : 1)),
          },
        ];
        monthsLabels[date.getMonth()] = true;
      }
      col++;
      if (col === 7) {
        col = 0;
        row++;
      }
    }

    if (this.selectedCell) {
      this.selectedCell = this.model
        .flat()
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        .find((cell) => isEqual(cell.date, this.selectedCell!.date));
    }

    const allEvents = this.calendars.flatMap(({ events }) =>
      Object.values(events).flatMap((months) =>
        Object.values(months).flatMap((days) => Object.values(days).flat(1)),
      ),
    );
    this.eventStyles = new Map([
      ...allEvents.map(
        (event) => [event, { ...event?.styles, ...this.eventCommonStyles }] as const,
      ),
      [undefined, this.eventCommonStyles],
    ]);
  }
}
